import { useMediaQuery } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StorePerformanceInterval, createStorePerformanceRequest } from '../../../../../constants';
import { useEventContext } from '../../../../../global-state/eventContext';
import { useThemeContext } from '../../../../../global-state/themeContext';
import { AveragePiecesPerReceiptPerformanceItem, useStorePerformance, useTransaction } from '../../../../../hooks';
import { Icons } from '../../../../../stories/atoms';
import { ContainerV2, RowFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { muiTheme } from '../../../../../theme';
import { homeTranslations } from '../../../../../translations';
import { formatAmountWithSeparator, isFailureResponse } from '../../../../../utils';
import { localeMap } from '../../../../../utils/language';
import { WidgetCardContent } from '../../../common/widgetCardContent/WidgetCardContent';
import WidgetChart from '../../../common/widgetChart/WidgetChart';
import {
  CurrentPeriodCard,
  getWidgetTimeFrameLabel,
  TrendCard,
} from '../../../common/widgetSummaryCards/WidgetSummaryCards';

export type PiecesPerReceiptWidgetProps = {
  interval: StorePerformanceInterval;
  currentDate: Date;
  isToday: boolean;
};

export const PiecesPerReceiptWidget: FC<PiecesPerReceiptWidgetProps> = observer(
  ({ interval, currentDate, isToday }) => {
    const { t, i18n } = useTranslation();

    const { fetchAveragePiecesPerReceiptData } = useTransaction();
    const { transactionEventsCount } = useEventContext();

    const { common, piecesPerReceipt: piecesPerReceipTranslations } = homeTranslations;
    const { mode } = useThemeContext();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

    const changeNote = isToday ? '' : t(common.changeVsCurrentNote.key, common.changeVsCurrentNote.defaultValue);

    const [today, setToday] = useState(0);
    const [lastWeek, setLastWeek] = useState(0);
    const [lastYear, setLastYear] = useState(0);
    const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

    const {
      performanceItems,
      performanceItemsLoading,
      performanceItemsError,

      filteredPerformanceItems,

      forecastPerformanceItems,
      triggerGetStorePerformance,
    } = useStorePerformance<AveragePiecesPerReceiptPerformanceItem>();

    const itemValueResolver = (item: AveragePiecesPerReceiptPerformanceItem) => {
      return item.amount;
    };

    const fetchData = useCallback(async () => {
      const request = createStorePerformanceRequest({
        interval,
        currentDate,
      });

      const response = await fetchAveragePiecesPerReceiptData(request);

      if (isFailureResponse(response)) {
        throw new Error();
      }

      setToday(response.data.today);
      setLastWeek(response.data.lastWeek);
      setLastYear(response.data.lastYear);
      setLastUpdated(response.data.lastUpdated);

      return response.data.piecesPerReceipt;
    }, [interval, currentDate]);

    const formatAmount = (value: number) => {
      return `${Intl.NumberFormat(i18n.language, { notation: 'compact' }).format(value)}`;
    };

    const triggerGetStorePerformanceRef = useRef(triggerGetStorePerformance);
    useEffect(() => {
      triggerGetStorePerformanceRef.current = triggerGetStorePerformance;
    }, [triggerGetStorePerformance]);

    const fetchWidgetData = useCallback(() => {
      triggerGetStorePerformanceRef.current({
        interval: interval,
        currentDate: currentDate,
        isToday: isToday,
        getDataAction: fetchData,
        getHasValue: (item) => item.amount !== 0,
      });
    }, [interval, currentDate, isToday, fetchData]);

    const weekLabel = t(
      homeTranslations.datePicker.weekLabel.key,
      homeTranslations.datePicker.weekLabel.defaultValue,
    );
    const dateLocale = localeMap[i18n.language] ?? localeMap['en-US'];
    const timeFrame = useMemo(
      () => getWidgetTimeFrameLabel(interval, currentDate, weekLabel, dateLocale),
      [interval, currentDate, weekLabel, dateLocale],
    );

    useEffect(() => {
      fetchWidgetData();
    }, [fetchWidgetData, transactionEventsCount]);

    const isNoData = !filteredPerformanceItems.some((item) => item !== null);

    const backgroundColor = mode === 'dark' ? 'backgroundSelectedHover' : 'white';

    return (
      <WidgetCardContent
        headerIcon={Icons.SALESEFFICIENCY}
        headerText={t(piecesPerReceipTranslations.title.key, piecesPerReceipTranslations.title.defaultValue)}
        lastUpdatedDate={lastUpdated}
        isLoading={performanceItemsLoading}
        isError={performanceItemsError}
        errorText={t(piecesPerReceipTranslations.error.key, piecesPerReceipTranslations.error.defaultValue)}
        errorRetryHandler={fetchWidgetData}
        isNoData={isNoData}
        subHeaderText={t(
          piecesPerReceipTranslations.subTitle.key,
          piecesPerReceipTranslations.subTitle.defaultValue,
        )}
        content={
          <ContainerV2
            data-testid="pieces-per-receipt-wrapper"
            height="100%"
            display="flex"
            flexDirection="column"
            minHeight={[0]}
          >
            <RowFlexContainer alignItems="stretch" gap={[2]} padding={[4, 5, 0, 5]}>
              <CurrentPeriodCard
                backgroundColor={backgroundColor}
                tooltipText={t(
                  piecesPerReceipTranslations.currentPeriodTooltip.key,
                  piecesPerReceipTranslations.currentPeriodTooltip.defaultValue,
                )}
                tooltipTestId="pieces-per-receipt-current-info-icon"
                value={today.toFixed(2)}
                timeFrame={timeFrame}
              />

              <TrendCard
                originalAmount={today}
                previousAmount={lastWeek}
                label={t(common.lastWeek.key, common.lastWeek.defaultValue)}
                backgroundColor={backgroundColor}
                hideTrendText={isToday}
                tooltipText={t(
                  piecesPerReceipTranslations.lastWeekTooltip.key,
                  piecesPerReceipTranslations.lastWeekTooltip.defaultValue,
                  { changeNote },
                )}
                tooltipTestId="pieces-per-receipt-last-week-info-icon"
                valueFormatter={(value) => value.toFixed(2)}
              />
              <TrendCard
                originalAmount={today}
                previousAmount={lastYear}
                label={t(common.lastYear.key, common.lastYear.defaultValue)}
                backgroundColor={backgroundColor}
                hideTrendText={isToday}
                tooltipText={t(
                  piecesPerReceipTranslations.lastYearTooltip.key,
                  piecesPerReceipTranslations.lastYearTooltip.defaultValue,
                  { changeNote },
                )}
                tooltipTestId="pieces-per-receipt-last-year-info-icon"
                valueFormatter={(value) => value.toFixed(2)}
              />
            </RowFlexContainer>

            <ContainerV2 data-testid="chart" style={{ flex: 1, minHeight: 0 }}>
              <WidgetChart
                type="bar"
                performanceItemsChartName={t(
                  piecesPerReceipTranslations.toolTip.key,
                  piecesPerReceipTranslations.toolTip.defaultValue,
                )}
                performanceItems={performanceItems}
                filteredPerformanceItems={filteredPerformanceItems}
                forecastPerformanceItems={forecastPerformanceItems}
                itemValueResolver={itemValueResolver}
                interval={interval}
                showXAxis={true}
                showYAxis={false}
                tooltipFormatter={(value) => formatAmountWithSeparator(value)}
                dataLabel={{
                  enabled: true,
                  enabledOnSeries: [0, 2],
                  offsetY: -28,
                  style: {
                    fontSize: isMobile ? '8px' : '12px',
                    fontWeight: '100',
                    colors: [mode === 'dark' ? muiTheme.color.white : muiTheme.color.primary],
                  },
                  formatter: function (value) {
                    if (!value) return '';

                    return formatAmount(Number.parseFloat(value.toString()));
                  },
                }}
              />
            </ContainerV2>
          </ContainerV2>
        }
        toolTipText={t(piecesPerReceipTranslations.toolTip.key, piecesPerReceipTranslations.toolTip.defaultValue)}
      />
    );
  },
);
